import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, HardHat } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSite } from "../context/SiteContext";
import { api, track, formatApiErrorDetail } from "../lib/api";

const STEPS = ["service", "zip", "description", "name", "phone", "email", "confirm"];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [flow, setFlow] = useState(null);
  const [lead, setLead] = useState({});
  const [busy, setBusy] = useState(false);
  const { L, services } = useSite();
  const C = L.chat;
  const serviceOptions = [...services.map((s) => s.title), "Other Service"];
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const bot = (text, options) => setMessages((m) => [...m, { from: "bot", text, options }]);
  const userMsg = (text) => setMessages((m) => [...m, { from: "user", text }]);

  const openChat = () => {
    setOpen(true);
    track("chat_opened");
    if (messages.length === 0) {
      bot(C.welcome, ["Request an Estimate", ...serviceOptions]);
    }
  };

  const startFlow = (service) => {
    setLead({ service });
    if (service) {
      setFlow("zip");
      bot(C.askZip(service));
    } else {
      setFlow("service");
      bot(C.askService, serviceOptions);
    }
  };

  const submitLead = async (data) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", data.name);
      fd.append("phone", data.phone);
      fd.append("email", data.email || "");
      fd.append("zip_code", data.zip || "");
      fd.append("service", data.service);
      fd.append("description", data.description || "");
      fd.append("source", "chat");
      await api.post("/estimate", fd);
      track("chat_lead_generated");
      bot(C.success);
      setFlow(null);
    } catch (e) {
      bot(formatApiErrorDetail(e.response?.data?.detail));
    }
    setBusy(false);
  };

  const advance = (value) => {
    const next = { ...lead };
    if (flow === "service") { next.service = value; bot(C.askZipShort); setFlow("zip"); }
    else if (flow === "zip") { next.zip = value; bot(C.askDesc); setFlow("description"); }
    else if (flow === "description") { next.description = value; bot(C.askName); setFlow("name"); }
    else if (flow === "name") { next.name = value; bot(C.askPhone(value)); setFlow("phone"); }
    else if (flow === "phone") {
      if (value.replace(/\D/g, "").length < 7) { bot(C.badPhone); return; }
      next.phone = value; bot(C.askEmail); setFlow("email");
    } else if (flow === "email") {
      const v = value.toLowerCase();
      next.email = (v === "skip" || v === "omitir") ? "" : value;
      bot(C.confirm(next), ["Yes, submit it", "No, start over"]);
      setFlow("confirm");
    } else if (flow === "confirm") {
      const v = value.toLowerCase();
      if (v.startsWith("yes") || v.startsWith("sí") || v.startsWith("si")) { submitLead(next); }
      else { setLead({}); setFlow("service"); bot(C.restart, serviceOptions); return; }
    }
    setLead(next);
  };

  const handleOption = (opt) => {
    userMsg(C.opt[opt] || opt);
    if (flow === "service" || flow === "confirm") { advance(opt); return; }
    if (opt === "Request an Estimate") { startFlow(null); return; }
    if (opt === "Other Service") { startFlow(null); return; }
    startFlow(opt);
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    userMsg(text);
    if (flow && STEPS.includes(flow)) { advance(text); return; }
    const lower = text.toLowerCase();
    const hit = C.faq.find((f) => lower.includes(f.q));
    bot(hit ? hit.a : C.fallback, ["Request an Estimate"]);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[380px] bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
            style={{ height: "min(560px, calc(100vh - 140px))" }}
            data-testid="chat-window"
          >
            <div className="bg-[#0B1B3D] text-white px-5 py-4 flex items-center gap-3">
              <span className="w-9 h-9 bg-[#005AE0] flex items-center justify-center"><HardHat className="w-4 h-4" /></span>
              <div>
                <p className="font-heading font-bold text-sm">{C.title}</p>
                <p className="text-[11px] text-blue-300">{C.status}</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="ml-auto text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {messages.map((m, i) => (
                <div key={i}>
                  <div className={`max-w-[85%] px-4 py-2.5 text-sm whitespace-pre-line leading-relaxed ${
                    m.from === "bot" ? "bg-white border border-slate-200 text-slate-800" : "bg-[#005AE0] text-white ml-auto"}`}>
                    {m.text}
                  </div>
                  {m.options && i === messages.length - 1 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {m.options.map((o) => (
                        <button key={o} onClick={() => handleOption(o)} data-testid={`chat-option-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="text-xs font-heading font-semibold px-3 py-2 border border-[#005AE0] text-[#005AE0] hover:bg-[#005AE0] hover:text-white transition-colors">
                          {C.opt[o] || o}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="border-t border-slate-200 p-3 flex gap-2 bg-white">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder={C.placeholder}
                data-testid="chat-input"
                className="flex-1 h-11 px-3 border border-slate-300 text-sm outline-none focus:border-[#005AE0]"
              />
              <button onClick={handleSend} data-testid="chat-send-btn" disabled={busy}
                className="w-11 h-11 bg-[#005AE0] text-white flex items-center justify-center hover:bg-[#0A66F5] transition-colors disabled:opacity-50">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => (open ? setOpen(false) : openChat())}
        data-testid="chat-toggle-btn"
        aria-label="Chat with Noyola Assistant"
        className="fixed bottom-5 right-4 sm:right-6 z-[60] w-14 h-14 rounded-full bg-[#005AE0] text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform duration-200"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
};

